import {GMI, STATE, CAR_AND_DRIVER} from '../../constants';
import request from '../request';
import reservations from '../../services/cda/reservations';
import {types} from "../../utils/js/types";
import {batchActions} from "../../utils/redux";


const {TRAVELER_INFORMATION} = STATE.UI.CAR_AND_DRIVER;

const FIELD_NAMES = CAR_AND_DRIVER.TRAVELER_INFORMATION;

/*************************** PRIVATE METHODS ***************************/

/**
 * @private
 * @name setFieldValue
 * @params {String} fieldName
 * @params {String} value
 * @returns {Object}
 */
function setFieldValue (fieldName, value = "") {
  return {
    type: GMI.SET_GMI_STATE,
    path: [...TRAVELER_INFORMATION.PASSENGER, fieldName],
    value
  };
}

/**
 * @private
 * @desc Dispatch all relevant travelerInfo actions from session object
 * @param {Object} args
 * @returns {Function}
 */
function setTravelerInfoState (args = {}) {
  return (dispatch) => {
    const {
      passenger = {}
    } = args;
    const batchedActions = [];
    Object.values(FIELD_NAMES).forEach( fieldName => {
      const sessionValue = passenger[fieldName] || '';
      batchedActions.push(setFieldValue(fieldName, sessionValue));
    });
    if(batchedActions.length !== 0) {
      dispatch(batchActions(...batchedActions));
    }
  };
}

/**
 * Exported for test purposes
 * @private
 * @desc Copy passenger values into booker object, return both booker & passenger
 * @param {Object} passenger
 * @returns {Object}
 */
export function prepareTravelerInfoPayload (passenger) {
  return {
    booker: Object.assign({}, passenger),
    passenger
  };
}

/*************************** PUBLIC METHODS ***************************/

export const travelerInfoState = {
  /**
   * @name set
   * @desc A global setter for all carAndDriver.travelerInfo payload data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.carAndDriver.travelerInfoState
   * @example
   *
   * // set booking type
   * store.dispatch(actions.carAndDriver.travelerInfoState.set('first_name', {value:'Bob'}));
   *
   */
  set (type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (Object.values(FIELD_NAMES).indexOf(type) > -1) {
          dispatch(setFieldValue(type, args.value));
        } else {
          console.warn(`${type} is not a supported state type`);
        }
      } else {
        dispatch(setTravelerInfoState(args));
      }
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for all carAndDriver.travelerInfo payload data
   * @param {String} [type]
   * @returns {Function}
   * @memberof actions.carAndDriver.travelerInfoState
   * @example
   *
   * // unset location
   * store.dispatch(actions.carAndDriver.travelerInfoState.unset('first_name'));
   */
  unset (type = null) {
    return dispatch => {
      if (types(type).isString) {
        if (Object.values(FIELD_NAMES).indexOf(type) > -1) {
          dispatch(setFieldValue(type));
        } else {
          console.warn(`${type} is not a supported state type`);
        }
      } else {
        //TODO: set up batched updates like in reservations.initiate.set
        console.warn(`carAndDriver.travelerInfo.unset batch updates not supported yet`);
      }
    };
  }
};

/**
 * @name travelerInformation
 * @desc Make the CDA travelerInformation call with state data;
 * @param {Object} [data] {params} Will get merged with getState().getIn(TRAVELER_INFORMATION).toJS() object
 * @param {Object} [requestOpts]
 * @returns {Function}
 * @memberof actions.carAndDriver
 * @example
 * store.dispatch(actions.carAndDriver.travelerInformation());
 */
export function travelerInformation(data, requestOpts = {}) {
  return (dispatch, getState) => {
    // transform payload to something deem/cda expects
    const payload = prepareTravelerInfoPayload(getState().getIn(TRAVELER_INFORMATION.PASSENGER).toJS());
    if (!types(data).isObject) data = {};
    Object.assign(data, {payload});
    return dispatch(request({
      data,
      service: reservations.travelerInformation,
      opts: requestOpts
    }));
  };
}

